import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { useExerciseLibrary } from '../components/context/ExerciseLibraryContext';
import DayTimeline from '../components/dashboard/DayTimeline';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dumbbell, Apple, Moon, Calendar, Flame, Droplets, TrendingUp,
  ChevronRight, Sparkles, Zap, Trophy, Play, CheckCircle, Clock,
  Sun, Sunrise, Sunset, Target, ArrowRight, RefreshCw, Plus,
  Utensils, Coffee, Pizza, Cookie, MapPin, User, AlertCircle, Check,
  Info, ExternalLink, Activity, Heart, ShoppingCart, Scale, Wrench,
  AlertTriangle, ClipboardList, List, LayoutList
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const TodaysPlanDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showExerciseDetail, findExercise } = useExerciseLibrary();
  const [dailyPlan, setDailyPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nutritionLogged, setNutritionLogged] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0 });
  const [hydrationLogged, setHydrationLogged] = useState(0);
  const [showWorkoutDetails, setShowWorkoutDetails] = useState(false);
  const [auraScore, setAuraScore] = useState(0);
  
  // Track completed items - now synced with API
  const [completedExercises, setCompletedExercises] = useState({});
  const [completedRecovery, setCompletedRecovery] = useState({});
  const [todayPoints, setTodayPoints] = useState(0);

  // ========== NEW: Integrated System States ==========
  const [goalsData, setGoalsData] = useState(null);
  const [readinessScore, setReadinessScore] = useState(null);
  const [externalWorkouts, setExternalWorkouts] = useState({ today: [], total_today: 0 });
  const [trainingProfile, setTrainingProfile] = useState(null);
  const [showExternalWorkoutDialog, setShowExternalWorkoutDialog] = useState(false);
  
  // View mode: 'cards' (original) or 'timeline' (new chronological view)
  const [viewMode, setViewMode] = useState('timeline');

  // Handle showing exercise details
  const handleExerciseInfo = (e, exerciseName) => {
    e.stopPropagation(); // Prevent triggering the toggle
    const exercise = findExercise(exerciseName);
    if (exercise) {
      showExerciseDetail(exercise);
    } else {
      toast.info(`No detailed info for "${exerciseName}" yet`);
    }
  };

  // Check if user has equipment for an exercise
  const checkEquipmentWarning = (exerciseName) => {
    if (!trainingProfile?.equipment_access) return null;
    const exercise = findExercise(exerciseName);
    if (!exercise || exercise.equipment === 'none') return null;
    
    const userEquipment = trainingProfile.equipment_access;
    if (exercise.equipment === 'basic' && userEquipment === 'none') {
      return 'Requires basic equipment';
    }
    if (exercise.equipment === 'full_gym' && userEquipment !== 'full_gym') {
      return 'Requires gym equipment';
    }
    return null;
  };

  // Fetch completed activities from API on load
  const fetchCompletedActivities = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/hydration/activity/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Build lookup maps from completed_keys
      const exercises = {};
      const recovery = {};
      
      (response.data.completed_keys || []).forEach(key => {
        if (key.startsWith('pregame_') || key.startsWith('postgame_') || key.startsWith('exercise_')) {
          exercises[key] = true;
        } else if (key.startsWith('recovery_')) {
          recovery[key] = true;
        }
      });
      
      setCompletedExercises(exercises);
      setCompletedRecovery(recovery);
      setTodayPoints(response.data.total_points || 0);
    } catch (error) {
      console.error('Failed to fetch completed activities:', error);
      // Fall back to localStorage
      const today = format(new Date(), 'yyyy-MM-dd');
      const savedEx = localStorage.getItem('aura_today_exercises');
      const savedRec = localStorage.getItem('aura_today_recovery');
      if (savedEx) {
        const data = JSON.parse(savedEx);
        if (data.date === today) setCompletedExercises(data.items);
      }
      if (savedRec) {
        const data = JSON.parse(savedRec);
        if (data.date === today) setCompletedRecovery(data.items);
      }
    }
  };

  const toggleExercise = async (exerciseKey, exerciseName = 'Exercise') => {
    const isCompleting = !completedExercises[exerciseKey];
    
    // Optimistic update
    setCompletedExercises(prev => ({
      ...prev,
      [exerciseKey]: isCompleting
    }));
    
    try {
      const token = localStorage.getItem('aura_token');
      
      if (isCompleting) {
        // Complete the activity
        const response = await axios.post(`${API}/hydration/activity/complete`, {
          activity_type: 'exercise',
          activity_key: exerciseKey,
          activity_name: exerciseName,
          points: 10
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success(response.data.message || 'Exercise completed! +10 points');
        setTodayPoints(response.data.today_total_points || todayPoints + 10);
      } else {
        // Undo completion
        await axios.delete(`${API}/hydration/activity/${exerciseKey}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.info('Exercise unmarked');
        setTodayPoints(prev => Math.max(0, prev - 10));
      }
    } catch (error) {
      console.error('Failed to update exercise:', error);
      // Revert on error
      setCompletedExercises(prev => ({
        ...prev,
        [exerciseKey]: !isCompleting
      }));
      toast.error('Failed to save progress');
    }
  };
  
  const toggleRecovery = async (recoveryKey, recoveryName = 'Recovery activity') => {
    const isCompleting = !completedRecovery[recoveryKey];
    
    // Optimistic update
    setCompletedRecovery(prev => ({
      ...prev,
      [recoveryKey]: isCompleting
    }));
    
    try {
      const token = localStorage.getItem('aura_token');
      
      if (isCompleting) {
        const response = await axios.post(`${API}/hydration/activity/complete`, {
          activity_type: 'recovery',
          activity_key: recoveryKey,
          activity_name: recoveryName,
          points: 5
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success(response.data.message || 'Recovery logged! +5 points');
        setTodayPoints(response.data.today_total_points || todayPoints + 5);
      } else {
        await axios.delete(`${API}/hydration/activity/${recoveryKey}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.info('Recovery unmarked');
        setTodayPoints(prev => Math.max(0, prev - 5));
      }
    } catch (error) {
      console.error('Failed to update recovery:', error);
      setCompletedRecovery(prev => ({
        ...prev,
        [recoveryKey]: !isCompleting
      }));
      toast.error('Failed to save progress');
    }
  };

  useEffect(() => {
    fetchDailyPlan();
    fetchNutritionLogged();
    fetchHydrationLogged();
    fetchAuraScore();
    fetchCompletedActivities();  // NEW: Fetch from API
    // NEW: Fetch integrated system data
    fetchGoalsData();
    fetchReadinessScore();
    fetchExternalWorkouts();
    fetchTrainingProfile();
  }, []);

  const fetchDailyPlan = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/intelligence/daily-plan`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyPlan(response.data);
    } catch (error) {
      console.error('Failed to fetch daily plan:', error);
      toast.error('Failed to load your daily plan');
    } finally {
      setLoading(false);
    }
  };

  // ========== NEW: Integrated System Fetch Functions ==========
  
  const fetchGoalsData = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/goals/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoalsData(response.data);
    } catch (error) {
      console.error('Failed to fetch goals:', error);
    }
  };

  const fetchReadinessScore = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/athlete/readiness-score`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReadinessScore(response.data);
    } catch (error) {
      console.error('Failed to fetch readiness:', error);
    }
  };

  const fetchExternalWorkouts = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/athlete-profile/external-training-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to today's workouts
      const today = format(new Date(), 'yyyy-MM-dd');
      const todayWorkouts = (response.data.history || []).filter(w => 
        w.date === today || w.date?.startsWith(today)
      );
      setExternalWorkouts({
        today: todayWorkouts,
        total_today: todayWorkouts.length,
        all_history: response.data.history || []
      });
    } catch (error) {
      console.error('Failed to fetch external workouts:', error);
    }
  };

  const fetchTrainingProfile = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/athlete-profile/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainingProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch training profile:', error);
    }
  };

  const fetchNutritionLogged = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const today = format(new Date(), 'yyyy-MM-dd');
      const response = await axios.get(`${API}/nutrition/daily-log?date=${today}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const totals = response.data.totals || {};
      setNutritionLogged({
        calories: totals.calories || 0,
        protein: totals.protein || 0,
        carbs: totals.carbs || 0,
        fat: totals.fat || 0,
      });
    } catch (error) {
      console.error('Failed to fetch nutrition:', error);
    }
  };

  const fetchHydrationLogged = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const today = format(new Date(), 'yyyy-MM-dd');
      const response = await axios.get(`${API}/hydration/today?date=${today}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHydrationLogged(response.data.total_oz || 0);
    } catch (error) {
      console.error('Failed to fetch hydration:', error);
    }
  };

  const fetchAuraScore = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/badges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuraScore(response.data.total_points || 0);
    } catch (error) {
      console.error('Failed to fetch aura score:', error);
    }
  };

  const quickLogHydration = async (oz) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/hydration/log`, { amount_oz: oz }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHydrationLogged(prev => prev + oz);
      toast.success(`+${oz} oz logged`);
    } catch (error) {
      toast.error('Failed to log hydration');
    }
  };

  const getDayTypeIcon = (dayType) => {
    switch (dayType) {
      case 'game_day': return Trophy;
      case 'pre_game': return Target;
      case 'training': return Dumbbell;
      case 'rest': return Moon;
      default: return Sun;
    }
  };

  const getDayTypeColor = (dayType) => {
    switch (dayType) {
      case 'game_day': return 'from-green-500/20 to-green-600/20 border-green-500/30';
      case 'pre_game': return 'from-yellow-500/20 to-orange-500/20 border-yellow-500/30';
      case 'training': return 'from-aura-primary/20 to-purple-500/20 border-aura-primary/30';
      case 'rest': return 'from-blue-500/20 to-cyan-500/20 border-blue-500/30';
      default: return 'from-white/5 to-white/10 border-white/10';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-aura-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!dailyPlan) {
    return (
      <DashboardLayout>
        <div className="px-4 py-6 text-center">
          <AlertCircle className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Complete Your Profile</h2>
          <p className="text-aura-text-muted mb-4">We need a bit more info to create your personalized plan.</p>
          <Button onClick={() => navigate('/onboarding')} className="bg-aura-primary">
            Complete Onboarding
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const DayIcon = getDayTypeIcon(dailyPlan.day_type);
  const nutrition = dailyPlan.nutrition || {};
  const hydration = dailyPlan.hydration || {};
  const workout = dailyPlan.workout || {};
  const motivation = dailyPlan.motivation || {};

  const calorieProgress = nutrition.calories ? (nutritionLogged.calories / nutrition.calories) * 100 : 0;
  const hydrationProgress = hydration.target_oz ? (hydrationLogged / hydration.target_oz) * 100 : 0;

  return (
    <DashboardLayout>
      <div className="px-4 py-6 max-w-4xl mx-auto space-y-4">
        
        {/* Day Header with View Toggle */}
        <div className={`rounded-2xl bg-gradient-to-br ${getDayTypeColor(dailyPlan.day_type)} border p-4`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-aura-text-muted text-sm uppercase tracking-wider">
                {dailyPlan.day_of_week}, {format(new Date(dailyPlan.date), 'MMMM d')}
              </p>
              <h1 className="text-2xl font-heading font-bold text-white mt-1">
                {dailyPlan.day_message}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-xs text-aura-text-muted">AURA</p>
                <p className="text-xl font-bold text-aura-primary">{auraScore}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <DayIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          
          {/* Motivation */}
          <div className="mt-4 p-3 bg-black/20 rounded-lg">
            <p className="text-sm text-white/90 italic">"{motivation.message}"</p>
            <p className="text-xs text-aura-text-muted mt-1">Focus: {motivation.focus}</p>
          </div>
          
          {/* View Toggle */}
          <div className="mt-4 flex items-center justify-center gap-2">
            <Button
              size="sm"
              variant={viewMode === 'timeline' ? 'default' : 'ghost'}
              className={viewMode === 'timeline' ? 'bg-white/20' : 'hover:bg-white/10'}
              onClick={() => setViewMode('timeline')}
            >
              <LayoutList className="w-4 h-4 mr-1" />
              Timeline
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              className={viewMode === 'cards' ? 'bg-white/20' : 'hover:bg-white/10'}
              onClick={() => setViewMode('cards')}
            >
              <List className="w-4 h-4 mr-1" />
              Cards
            </Button>
          </div>
        </div>

        {/* Timeline View */}
        {viewMode === 'timeline' && (
          <DayTimeline date={dailyPlan.date} />
        )}

        {/* Cards View (Original) */}
        {viewMode === 'cards' && (
          <>
        {/* Today's Workout */}
        <Card className="bg-aura-surface border-white/10 overflow-hidden">
          <div className={`bg-gradient-to-r ${
            dailyPlan.day_type === 'game_day' ? 'from-green-500/20 to-emerald-500/20' :
            dailyPlan.day_type === 'pre_game' ? 'from-yellow-500/20 to-orange-500/20' :
            'from-aura-primary/20 to-purple-500/20'
          } p-4`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  dailyPlan.day_type === 'game_day' ? 'bg-green-500/30' :
                  dailyPlan.day_type === 'pre_game' ? 'bg-yellow-500/30' :
                  'bg-aura-primary/30'
                }`}>
                  {dailyPlan.day_type === 'game_day' ? (
                    <Trophy className="w-5 h-5 text-green-400" />
                  ) : (
                    <Dumbbell className="w-5 h-5 text-aura-primary" />
                  )}
                </div>
                <div>
                  <p className="text-xs text-aura-text-muted uppercase">
                    {dailyPlan.day_type === 'game_day' ? "Game Day Routine" : "Today's Workout"}
                  </p>
                  <p className="text-lg font-bold text-white">
                    {dailyPlan.day_type === 'game_day' 
                      ? 'Pre-Game + Post-Game' 
                      : (workout.template?.name || 'Rest Day')}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-white">{workout.duration || 0}</p>
                <p className="text-xs text-aura-text-muted">minutes</p>
              </div>
            </div>
          </div>
          
          <CardContent className="p-4">
            <p className="text-sm text-aura-text-muted mb-4">{workout.recommendation}</p>
            
            {/* Game Day: Show Pre-Game and Post-Game separately */}
            {dailyPlan.day_type === 'game_day' && workout.template?.pre_game && workout.template?.post_game ? (
              <div className="space-y-3 mb-4">
                {/* Pre-Game - CLICKABLE EXERCISES */}
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Sunrise className="w-4 h-4 text-green-400" />
                      <span className="font-medium text-green-400">Pre-Game Activation</span>
                    </div>
                    <span className="text-xs text-aura-text-muted">
                      {Object.keys(completedExercises).filter(k => k.startsWith('pregame_') && completedExercises[k]).length}/
                      {workout.template.pre_game.exercises?.length || 0}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {workout.template.pre_game.exercises?.map((ex, idx) => {
                      const exerciseKey = `pregame_${idx}_${ex.name}`;
                      const isCompleted = completedExercises[exerciseKey];
                      const hasLibraryEntry = findExercise(ex.name);
                      
                      return (
                        <div
                          key={idx}
                          className={`flex items-center gap-2 p-2 rounded transition-all ${
                            isCompleted ? 'bg-green-500/20' : 'hover:bg-white/5'
                          }`}
                        >
                          <button
                            onClick={() => toggleExercise(exerciseKey, ex.name)}
                            className="flex items-center gap-2 flex-1 text-left"
                          >
                            <div className={`w-5 h-5 rounded flex items-center justify-center ${
                              isCompleted ? 'bg-green-500 text-white' : 'bg-white/10'
                            }`}>
                              {isCompleted ? <Check className="w-3 h-3" /> : <span className="text-xs">{idx + 1}</span>}
                            </div>
                            <span className={`text-xs flex-1 ${isCompleted ? 'text-green-400 line-through' : 'text-aura-text-muted'}`}>
                              {ex.name}
                            </span>
                          </button>
                          {hasLibraryEntry && (
                            <button
                              onClick={(e) => handleExerciseInfo(e, ex.name)}
                              className="p-1 hover:bg-white/10 rounded transition-colors"
                              title="View exercise details"
                            >
                              <Info className="w-3.5 h-3.5 text-blue-400" />
                            </button>
                          )}
                          {isCompleted && <span className="text-xs text-green-400">Done!</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Post-Game - CLICKABLE EXERCISES */}
                <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Sunset className="w-4 h-4 text-purple-400" />
                      <span className="font-medium text-purple-400">Post-Game Recovery</span>
                    </div>
                    <span className="text-xs text-aura-text-muted">
                      {Object.keys(completedExercises).filter(k => k.startsWith('postgame_') && completedExercises[k]).length}/
                      {workout.template.post_game.exercises?.length || 0}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {workout.template.post_game.exercises?.map((ex, idx) => {
                      const exerciseKey = `postgame_${idx}_${ex.name}`;
                      const isCompleted = completedExercises[exerciseKey];
                      const hasLibraryEntry = findExercise(ex.name);
                      
                      return (
                        <div
                          key={idx}
                          className={`flex items-center gap-2 p-2 rounded transition-all ${
                            isCompleted ? 'bg-purple-500/20' : 'hover:bg-white/5'
                          }`}
                        >
                          <button
                            onClick={() => toggleExercise(exerciseKey, ex.name)}
                            className="flex items-center gap-2 flex-1 text-left"
                          >
                            <div className={`w-5 h-5 rounded flex items-center justify-center ${
                              isCompleted ? 'bg-purple-500 text-white' : 'bg-white/10'
                            }`}>
                              {isCompleted ? <Check className="w-3 h-3" /> : <span className="text-xs">{idx + 1}</span>}
                            </div>
                            <span className={`text-xs flex-1 ${isCompleted ? 'text-purple-400 line-through' : 'text-aura-text-muted'}`}>
                              {ex.name}
                            </span>
                          </button>
                          {hasLibraryEntry && (
                            <button
                              onClick={(e) => handleExerciseInfo(e, ex.name)}
                              className="p-1 hover:bg-white/10 rounded transition-colors"
                              title="View exercise details"
                            >
                              <Info className="w-3.5 h-3.5 text-blue-400" />
                            </button>
                          )}
                          {isCompleted && <span className="text-xs text-purple-400">Done!</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              /* Regular workout display - CLICKABLE EXERCISES */
              <>
                {workout.template && workout.template.exercises && (
                  <div className="space-y-2 mb-4">
                    {workout.template.exercises.map((exercise, idx) => {
                      const exerciseKey = `exercise_${idx}_${exercise.name}`;
                      const isCompleted = completedExercises[exerciseKey];
                      const hasLibraryEntry = findExercise(exercise.name);
                      const equipmentWarning = checkEquipmentWarning(exercise.name);
                      
                      return (
                        <div
                          key={idx}
                          className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                            isCompleted 
                              ? 'bg-green-500/20 border border-green-500/30' 
                              : equipmentWarning
                              ? 'bg-yellow-500/10 border border-yellow-500/30'
                              : 'bg-white/5 hover:bg-white/10 border border-transparent'
                          }`}
                        >
                          <button
                            onClick={() => toggleExercise(exerciseKey, exercise.name)}
                            className="flex items-center gap-3 flex-1"
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold transition-colors ${
                              isCompleted 
                                ? 'bg-green-500 text-white' 
                                : equipmentWarning
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-white/10 text-white'
                            }`}>
                              {isCompleted ? <Check className="w-4 h-4" /> : equipmentWarning ? <Wrench className="w-4 h-4" /> : idx + 1}
                            </div>
                            <div className="flex-1 text-left">
                              <p className={`text-sm ${isCompleted ? 'text-green-400 line-through' : 'text-white'}`}>
                                {exercise.name}
                              </p>
                              <p className="text-xs text-aura-text-muted">
                                {exercise.sets ? `${exercise.sets} x ${exercise.reps}` : `${exercise.duration} min`}
                              </p>
                              {equipmentWarning && !isCompleted && (
                                <p className="text-xs text-yellow-400 mt-0.5">
                                  <Wrench className="w-3 h-3 inline mr-1" />
                                  {equipmentWarning}
                                </p>
                              )}
                            </div>
                          </button>
                          {hasLibraryEntry && (
                            <button
                              onClick={(e) => handleExerciseInfo(e, exercise.name)}
                              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                              title="View exercise details & video"
                            >
                              <Info className="w-4 h-4 text-blue-400" />
                            </button>
                          )}
                          <div className={`text-xs ${isCompleted ? 'text-green-400' : 'text-aura-text-muted'}`}>
                            {isCompleted ? 'Done!' : hasLibraryEntry ? '' : 'Tap'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Progress indicator */}
                {workout.template?.exercises && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-aura-text-muted mb-1">
                      <span>Workout Progress</span>
                      <span>
                        {Object.values(completedExercises).filter(Boolean).length} / {workout.template.exercises.length}
                      </span>
                    </div>
                    <Progress 
                      value={(Object.values(completedExercises).filter(Boolean).length / workout.template.exercises.length) * 100} 
                      className="h-2"
                    />
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    className="flex-1 border-white/20 text-white"
                    onClick={() => navigate('/workouts')}
                  >
                    <Dumbbell className="w-4 h-4 mr-2" />
                    Training Matrix
                  </Button>
                  <Button variant="outline" className="border-white/20 text-white" onClick={() => navigate('/calendar-hub')}>
                    <Calendar className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Nutrition + Hydration Row */}
        <div className="grid grid-cols-2 gap-4">
          {/* Nutrition Card */}
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Apple className="w-5 h-5 text-green-400" />
                  <span className="text-sm font-medium text-white">Nutrition</span>
                </div>
                <Badge variant="outline" className={`text-xs ${
                  nutrition.is_game_day ? 'border-green-500 text-green-400' :
                  nutrition.is_training_day ? 'border-aura-primary text-aura-primary' :
                  'border-white/30 text-white/70'
                }`}>
                  {nutrition.is_game_day ? 'Game Day' : nutrition.is_training_day ? 'Training' : 'Rest'}
                </Badge>
              </div>
              
              <div className="mb-3">
                <div className="flex justify-between text-xs text-aura-text-muted mb-1">
                  <span>{nutritionLogged.calories} / {nutrition.calories} cal</span>
                  <span>{Math.round(calorieProgress)}%</span>
                </div>
                <Progress value={calorieProgress} className="h-2" />
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-white/5 rounded-lg">
                  <p className="text-xs text-aura-text-muted">Protein</p>
                  <p className="text-sm font-bold text-white">{nutritionLogged.protein}/{nutrition.protein}g</p>
                </div>
                <div className="p-2 bg-white/5 rounded-lg">
                  <p className="text-xs text-aura-text-muted">Carbs</p>
                  <p className="text-sm font-bold text-white">{nutritionLogged.carbs}/{nutrition.carbs}g</p>
                </div>
                <div className="p-2 bg-white/5 rounded-lg">
                  <p className="text-xs text-aura-text-muted">Fat</p>
                  <p className="text-sm font-bold text-white">{nutritionLogged.fat}/{nutrition.fat}g</p>
                </div>
              </div>
              
              {/* Goal-specific nutrition tips */}
              {nutrition.goal_notes && nutrition.goal_notes.length > 0 && (
                <div className="mt-3 p-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-xs text-green-400 font-medium mb-1">Goal-Based Tips</p>
                  <p className="text-xs text-aura-text-muted">
                    {nutrition.goal_notes[0]}
                  </p>
                </div>
              )}
              
              <Button 
                variant="outline" 
                className="w-full mt-3 border-white/20 text-white"
                onClick={() => navigate('/nutrition-pro')}
              >
                <Plus className="w-4 h-4 mr-2" />
                Log Meal
              </Button>
            </CardContent>
          </Card>

          {/* Hydration Card */}
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-blue-400" />
                  <span className="text-sm font-medium text-white">Hydration</span>
                </div>
                <span className="text-xs text-aura-text-muted">
                  {hydration.target_oz} oz goal
                </span>
              </div>
              
              <div className="mb-3">
                <div className="flex justify-between text-xs text-aura-text-muted mb-1">
                  <span>{hydrationLogged} / {hydration.target_oz} oz</span>
                  <span>{Math.round(hydrationProgress)}%</span>
                </div>
                <Progress value={hydrationProgress} className="h-2 bg-blue-500/20 [&>div]:bg-blue-500" />
              </div>
              
              {/* Quick add buttons */}
              <div className="grid grid-cols-4 gap-1">
                {[8, 12, 16, 24].map(oz => (
                  <button
                    key={oz}
                    onClick={() => quickLogHydration(oz)}
                    className="p-2 text-xs bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 font-medium transition-colors"
                  >
                    +{oz}oz
                  </button>
                ))}
              </div>
              
              {hydration.pre_workout_oz > 0 && (
                <p className="text-xs text-blue-300 mt-2 text-center">
                  Pre-workout: {hydration.pre_workout_oz}oz recommended
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ========== INTEGRATED SYSTEM WIDGETS ========== */}
        
        {/* Goals Widget - Links to /goals */}
        {goalsData && goalsData.active_goal && (
          <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Active Goal</p>
                    <p className="text-xs text-amber-400 font-medium">
                      {goalsData.active_goal.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {goalsData.target_calories && (
                    <p className="text-xs text-aura-text-muted">
                      Target: <span className="text-white font-medium">{goalsData.target_calories} cal</span>
                    </p>
                  )}
                  {goalsData.weight_progress && (
                    <p className="text-xs text-aura-text-muted mt-0.5">
                      {goalsData.current_weight} → {goalsData.target_weight} lbs
                    </p>
                  )}
                </div>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/20"
                  onClick={() => navigate('/goals')}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              {goalsData.protein_goal && (
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center p-2 bg-white/5 rounded">
                    <span className="text-aura-text-muted">Protein</span>
                    <p className="text-white font-medium">{goalsData.protein_goal}g</p>
                  </div>
                  <div className="text-center p-2 bg-white/5 rounded">
                    <span className="text-aura-text-muted">Carbs</span>
                    <p className="text-white font-medium">{goalsData.carbs_goal}g</p>
                  </div>
                  <div className="text-center p-2 bg-white/5 rounded">
                    <span className="text-aura-text-muted">Fat</span>
                    <p className="text-white font-medium">{goalsData.fat_goal}g</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* No Goals Set - CTA */}
        {goalsData && !goalsData.active_goal && (
          <Card className="bg-white/5 border-dashed border-white/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Target className="w-5 h-5 text-aura-text-muted" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">Set Your Goals</p>
                  <p className="text-xs text-aura-text-muted">
                    Define targets to unlock personalized nutrition & workout plans
                  </p>
                </div>
                <Button 
                  size="sm" 
                  className="bg-aura-primary hover:bg-aura-primary-hover"
                  onClick={() => navigate('/goals')}
                >
                  Set Goals
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Readiness Score Widget - Links to /readiness */}
        {readinessScore && (
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    readinessScore.score >= 85 ? 'bg-green-500/20' :
                    readinessScore.score >= 70 ? 'bg-yellow-500/20' :
                    readinessScore.score >= 50 ? 'bg-orange-500/20' :
                    'bg-red-500/20'
                  }`}>
                    <Activity className={`w-6 h-6 ${
                      readinessScore.score >= 85 ? 'text-green-400' :
                      readinessScore.score >= 70 ? 'text-yellow-400' :
                      readinessScore.score >= 50 ? 'text-orange-400' :
                      'text-red-400'
                    }`} />
                  </div>
                  <div>
                    <p className="text-xs text-aura-text-muted">Today's Readiness</p>
                    <div className="flex items-center gap-2">
                      <span className={`text-2xl font-bold ${
                        readinessScore.score >= 85 ? 'text-green-400' :
                        readinessScore.score >= 70 ? 'text-yellow-400' :
                        readinessScore.score >= 50 ? 'text-orange-400' :
                        'text-red-400'
                      }`}>
                        {readinessScore.score}
                      </span>
                      <Badge className={`text-xs ${
                        readinessScore.score >= 85 ? 'bg-green-500/20 text-green-400' :
                        readinessScore.score >= 70 ? 'bg-yellow-500/20 text-yellow-400' :
                        readinessScore.score >= 50 ? 'bg-orange-500/20 text-orange-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {readinessScore.tier || (
                          readinessScore.score >= 85 ? 'Elite' :
                          readinessScore.score >= 70 ? 'Good' :
                          readinessScore.score >= 50 ? 'Moderate' :
                          'Rest Needed'
                        )}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  {readinessScore.recommendation && (
                    <p className="text-xs text-aura-text-muted max-w-[150px]">
                      {readinessScore.recommendation}
                    </p>
                  )}
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-aura-text-muted hover:text-white mt-1"
                    onClick={() => navigate('/readiness')}
                  >
                    Details <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
              {readinessScore.factors && (
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {Object.entries(readinessScore.factors).slice(0, 4).map(([key, value]) => (
                    <div key={key} className="text-center p-2 bg-white/5 rounded">
                      <p className="text-xs text-aura-text-muted capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="text-sm font-medium text-white">{value}%</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* External Training Widget - Links to /external-workout */}
        <Card className="bg-aura-surface border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">External Training</p>
                  <p className="text-xs text-aura-text-muted">
                    {externalWorkouts.total_today > 0 
                      ? `${externalWorkouts.total_today} session${externalWorkouts.total_today > 1 ? 's' : ''} logged today`
                      : 'Personal trainer, team practice, S&C'
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  size="sm" 
                  className="bg-indigo-500 hover:bg-indigo-600"
                  onClick={() => navigate('/external-workout')}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Log
                </Button>
              </div>
            </div>
            {externalWorkouts.today.length > 0 && (
              <div className="mt-3 space-y-1">
                {externalWorkouts.today.slice(0, 2).map((workout, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-indigo-500/10 rounded text-xs">
                    <span className="text-white">{workout.workout_type}</span>
                    <span className="text-indigo-400">{workout.duration_minutes} min</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Training Profile Equipment Warnings - Shows when relevant */}
        {trainingProfile && trainingProfile.injuries && trainingProfile.injuries.length > 0 && (
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <p className="text-xs text-red-400 font-medium">Active Injury Alert</p>
              </div>
              <p className="text-xs text-aura-text-muted mt-1">
                {trainingProfile.injuries.join(', ')} - Workouts will be modified
              </p>
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-red-400 text-xs mt-2 h-6 px-2"
                onClick={() => navigate('/training-profile')}
              >
                Update Profile
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Recovery Recommendations - CLICKABLE */}
        {dailyPlan.recovery && dailyPlan.recovery.length > 0 && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
                <Moon className="w-4 h-4 text-purple-400" />
                Recovery Plan
                {dailyPlan.context?.goals?.some(g => 
                  g.toLowerCase().includes('muscle') || g.toLowerCase().includes('build')
                ) && (
                  <Badge variant="outline" className="border-green-500/30 text-green-400 text-xs ml-2">
                    Muscle Building Focus
                  </Badge>
                )}
                <span className="ml-auto text-xs text-aura-text-muted">
                  {Object.values(completedRecovery).filter(Boolean).length}/{dailyPlan.recovery.length} done
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {dailyPlan.recovery.map((item, idx) => {
                  const recoveryKey = `recovery_${idx}_${item.activity}`;
                  const isCompleted = completedRecovery[recoveryKey];
                  const hasLibraryEntry = findExercise(item.activity);
                  
                  return (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg transition-all ${
                        isCompleted 
                          ? 'bg-purple-500/20 border border-purple-500/30' 
                          : 'bg-white/5 hover:bg-white/10 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => toggleRecovery(recoveryKey, item.activity)}
                          className="flex items-center gap-3 flex-1 text-left"
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                            isCompleted ? 'bg-purple-500 text-white' : 'bg-white/10'
                          }`}>
                            {isCompleted ? <Check className="w-4 h-4" /> : <Moon className="w-4 h-4 text-purple-400" />}
                          </div>
                          <div className="w-14 text-xs text-aura-text-muted">{item.time}</div>
                          <div className="flex-1">
                            <p className={`text-sm ${isCompleted ? 'text-purple-400 line-through' : 'text-white'}`}>
                              {item.activity}
                            </p>
                            {item.reason && (
                              <p className="text-xs text-aura-text-muted mt-0.5">
                                → {item.reason}
                              </p>
                            )}
                          </div>
                        </button>
                        {hasLibraryEntry && (
                          <button
                            onClick={(e) => handleExerciseInfo(e, item.activity)}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            title="View details & video"
                          >
                            <Info className="w-4 h-4 text-blue-400" />
                          </button>
                        )}
                        {item.duration && (
                          <Badge variant="outline" className="border-purple-500/30 text-purple-400 text-xs">
                            {item.duration} min
                          </Badge>
                        )}
                        {item.priority === 'high' && !isCompleted && (
                          <Badge className="bg-red-500/20 text-red-400 text-xs">Priority</Badge>
                        )}
                        {isCompleted && (
                          <span className="text-xs text-purple-400">Done!</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-3 border-white/20 text-white"
                onClick={() => navigate('/recovery')}
              >
                <Moon className="w-4 h-4 mr-2" />
                Full Recovery Log
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Meal Suggestions (if game day or pre-game) */}
        {nutrition.meal_suggestions && (nutrition.is_game_day || dailyPlan.context?.is_pre_game_day) && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
                <Utensils className="w-4 h-4 text-orange-400" />
                {nutrition.is_game_day ? 'Game Day Nutrition' : 'Pre-Game Fuel'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              {nutrition.meal_suggestions.pre_game && (
                <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <p className="text-xs text-orange-400 font-medium mb-1">Pre-Game Meal</p>
                  <p className="text-sm text-white">{nutrition.meal_suggestions.pre_game.focus}</p>
                  <p className="text-xs text-aura-text-muted mt-1">
                    Timing: {nutrition.meal_suggestions.pre_game.timing}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {nutrition.meal_suggestions.pre_game.examples?.map((ex, i) => (
                      <Badge key={i} variant="outline" className="text-xs border-orange-500/30 text-orange-300">
                        {ex}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {nutrition.meal_suggestions.post_game && (
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-xs text-green-400 font-medium mb-1">Post-Game Recovery</p>
                  <p className="text-sm text-white">{nutrition.meal_suggestions.post_game.focus}</p>
                  <p className="text-xs text-aura-text-muted mt-1">
                    Timing: {nutrition.meal_suggestions.post_game.timing}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Upcoming Games Alert */}
        {dailyPlan.games?.tomorrow?.length > 0 && (
          <Card className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">Game Tomorrow</p>
                  <p className="text-xs text-aura-text-muted">
                    vs {dailyPlan.games.tomorrow[0].opponent || 'Opponent'} - Get rest tonight!
                  </p>
                </div>
                <Button size="sm" variant="outline" className="border-yellow-500/30 text-yellow-400">
                  Prep Checklist
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-2">
          {[
            { icon: Dumbbell, label: 'Training', path: '/workouts', color: 'text-aura-primary' },
            { icon: Apple, label: 'Nutrition', path: '/nutrition-pro', color: 'text-green-400' },
            { icon: Moon, label: 'Recovery', path: '/recovery', color: 'text-purple-400' },
            { icon: Calendar, label: 'Schedule', path: '/calendar-hub', color: 'text-blue-400' },
          ].map((action, idx) => (
            <button
              key={idx}
              onClick={() => navigate(action.path)}
              className="p-3 bg-white/5 hover:bg-white/10 rounded-xl flex flex-col items-center gap-1 transition-colors"
            >
              <action.icon className={`w-5 h-5 ${action.color}`} />
              <span className="text-xs text-aura-text-muted">{action.label}</span>
            </button>
          ))}
        </div>

        {/* Workout Details Dialog */}
        <Dialog open={showWorkoutDetails} onOpenChange={setShowWorkoutDetails}>
          <DialogContent className="bg-aura-surface border-white/10 max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-white uppercase flex items-center gap-2">
                <Dumbbell className="w-5 h-5 text-aura-primary" />
                {workout.template?.name || 'Workout Details'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <p className="text-sm text-aura-text-muted">Duration</p>
                  <p className="text-lg font-bold text-white">{workout.duration} min</p>
                </div>
                <div>
                  <p className="text-sm text-aura-text-muted">Intensity</p>
                  <p className="text-lg font-bold text-white capitalize">{workout.template?.intensity || 'Moderate'}</p>
                </div>
                <div>
                  <p className="text-sm text-aura-text-muted">Focus</p>
                  <p className="text-lg font-bold text-white capitalize">
                    {workout.template?.focus?.join(', ') || 'General'}
                  </p>
                </div>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-aura-text-muted uppercase tracking-wider">Exercises</p>
                {workout.template?.exercises?.map((exercise, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <div className="w-8 h-8 rounded-lg bg-aura-primary/20 flex items-center justify-center text-sm font-bold text-aura-primary">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{exercise.name}</p>
                      <p className="text-xs text-aura-text-muted">
                        {exercise.sets ? `${exercise.sets} sets x ${exercise.reps}` : 
                         exercise.duration ? `${exercise.duration} min` : ''}
                        {exercise.rest ? ` • ${exercise.rest}s rest` : ''}
                      </p>
                    </div>
                    {exercise.equipment?.length > 0 && (
                      <Badge variant="outline" className="text-xs border-white/20 text-white/70">
                        {exercise.equipment.join(', ')}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
              
              <Button className="w-full bg-aura-primary hover:bg-aura-primary-hover" onClick={() => {
                setShowWorkoutDetails(false);
                navigate('/workouts');
              }}>
                <Play className="w-4 h-4 mr-2" />
                Go to Training
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TodaysPlanDashboard;
